import type {
	PermissionsMetadata,
	PermissionsMetadataDef,
} from './permission-metadata';
import { makePermissionMetadata } from './permission-metadata';
import type { PermissionKey } from './permission-types';

export interface PermissionSystem<P extends string, A extends string> {
	readonly permissionTypes: readonly P[];
	readonly actionTypes: readonly A[];
	readonly permissionsMetadata: PermissionsMetadata<P, A>;
	/**
	 * Permissions only allowed for superusers.
	 */
	readonly superPerms: readonly PermissionKey<P, A>[];
	/**
	 * A list of
	 * 1. superPerms
	 * 2. the perms that superPerms depend on
	 * 3. the perms that the perms in 2. depend on
	 * 4. repeat 3.
	 */
	readonly superPermDeps: readonly PermissionKey<P, A>[];
}

export type PermissionType<T extends PermissionSystem<string, string>> =
	T['permissionTypes'][number];

export type ActionType<T extends PermissionSystem<string, string>> =
	T['actionTypes'][number];

export interface PermissionSystemDef<P extends string, A extends string> {
	readonly permissionTypes: readonly P[];
	readonly actionTypes: readonly A[];
	readonly permissionsMetadata: PermissionsMetadataDef<P, A>;
	readonly superPerms: readonly PermissionKey<P, A>[];
}

export function makePermissionSystem<P extends string, A extends string>(
	systemDef: PermissionSystemDef<P, A>
): PermissionSystem<P, A> {
	const { permissionsMetadata, superPermDeps } = makePermissionMetadata(
		systemDef.permissionTypes,
		systemDef.actionTypes,
		systemDef.permissionsMetadata,
		systemDef.superPerms
	);
	return {
		permissionTypes: systemDef.permissionTypes,
		actionTypes: systemDef.actionTypes,
		superPerms: systemDef.superPerms,
		superPermDeps,
		permissionsMetadata,
	};
}
